import React from 'react';
import { get, isEmpty, isEqual } from 'lodash';

// local dependencies
import { PAGE_LEVEL_PRELOADER_TEMPLATES } from 'constants/common';
import { geoCordsFromAddress } from 'services/geocode';
import { mapWithStores as MapWithStoresView } from 'ui/views/widgets/mapWithStores';
import { withAsyncDependencies } from 'ui/hoc/withAsyncDependencies';
import * as pageLevelPreloader from 'modules/page-level-preloader';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MapWithStores } from 'ui/containers/MapWithStores';
 *
 * // render
 * <MapWithStores propName='propValue'/>
 */
class _MapWithStores extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            mapApiLoaded: ! isEmpty( props.asyncDependencies.google ),
            locations: [],
        };

        // load map locations on mount if Google map API is available
        if( this.state.mapApiLoaded ) {
            this.createMapLocations( props.locations );
        }
    }

    /**********************************************/

    /**
     * @desc Call geocode service to convert location address
     * to their respective latitude and longitude coordinates and other meta-data.
     */
    createMapLocations( locations = [] ) {
        log.debug( 'MapWithStores.createMapLocations()' );

        // get google map API
        const { google } = this.props.asyncDependencies;

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // resolve all location coordinate and meta-data
        Promise.all( locations.map( ( location ) => {

            // get address from the location information
            const _address = get( location, 'address', {} );
            const address = [ _address.line1, _address.line2, _address.city, _address.state, _address.zip ].join( ' ' );

            // make request to google api for coordinates
            return geoCordsFromAddress( google, address ).then( ( { lat, long } ) => {
                return {
                    id: location.id,
                    lat,
                    long,
                    name: location.name,
                    infoWindow: {
                        props: {
                            address,
                            openHours: get( location, 'openHours', {} )
                        }
                    }
                };
            } ).catch( () => { return null; } );
        } ) ).then( ( geoLocations ) => {

            // filter empty locations
			const _geoLocations = geoLocations.filter( ( geoLocation ) => {
				return null !== geoLocation;
			} );

            // update locations in the state
            this.setState( {
                mapApiLoaded: true,
                locations: _geoLocations,
            } );

            // hide page level preloader
            pageLevelPreloader.hide( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );
        } );
    }

    /**********************************************/

    /**
     * @desc Update view when component receives new props
     */
    UNSAFE_componentWillReceiveProps( nextProps ) {
        log.debug( 'MapWithStores.UNSAFE_componentWillReceiveProps()' );

        // when google map dependency is resolved, fetch locations for the map
		if( ! this.state.mapApiLoaded && ! isEmpty( nextProps.asyncDependencies.google ) ) {
            this.createMapLocations( nextProps.locations );
        } else if( this.state.mapApiLoaded && ! isEqual( nextProps.locations, this.props.locations ) ) {
            this.createMapLocations( nextProps.locations );
        }
    }
    
    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MapWithStoresView
                { ...this.props }
                locations = { this.state.locations }
            />
        );
    }
}

// set display name
_MapWithStores.displayName = '_MapWithStores';

// set default props
_MapWithStores.defaultProps = {
    id: null, // unique map id
};

// prop types
_MapWithStores.propTypes = {};

/**********************************************/

export const MapWithStores = withAsyncDependencies( _MapWithStores );
